import { useGetOrganizationAddressesQuery } from '@codezo/graphql';
import { MapPin, Pencil } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { AddressForm, EMPTY_ADDRESS, type AddressData, type AddressErrors } from './address-form';
import { Radio } from './radio';

/* ── Address comparison ──
 *
 * A saved address and one written on a quote or an order are separate records:
 * the latter is a copy of the values, with no id pointing back. So "is this the
 * same address" can only be answered on what a person typed.
 */

/** Anything address-shaped — a saved address, or a quote's/order's own copy. */
interface AddressLike {
	addressLine1?: string | null;
	addressLine2?: string | null;
	city?: string | null;
	stateCode?: string | null;
	postalCode?: string | null;
	countryCode?: string | null;
	googlePlaceId?: string | null;
	latitude?: number | null;
	longitude?: number | null;
}

export const toAddressData = (a: AddressLike): AddressData => ({
	addressLine1: a.addressLine1 ?? '',
	addressLine2: a.addressLine2 ?? '',
	city: a.city ?? '',
	stateCode: a.stateCode ?? '',
	postalCode: a.postalCode ?? '',
	countryCode: a.countryCode ?? '',
	// Carried through: the address is written back whole, so dropping these
	// would discard the geocoding on an otherwise untouched address.
	googlePlaceId: a.googlePlaceId ?? '',
	latitude: a.latitude ?? null,
	longitude: a.longitude ?? null,
});

const norm = (v?: string | null) => (v ?? '').trim().toLowerCase();

/** Whether two addresses describe the same place. */
export const sameAddress = (a: AddressLike, b: AddressLike) =>
	norm(a.addressLine1) === norm(b.addressLine1) &&
	norm(a.addressLine2) === norm(b.addressLine2) &&
	norm(a.postalCode) === norm(b.postalCode) &&
	norm(a.city) === norm(b.city) &&
	norm(a.stateCode) === norm(b.stateCode) &&
	norm(a.countryCode) === norm(b.countryCode);

/** The address on one line, for compact rows. */
const addressSummary = (a: AddressLike) =>
	[
		a.addressLine1,
		a.addressLine2,
		[a.postalCode, a.city].filter(Boolean).join(' '),
		a.countryCode,
	]
		.filter((part) => !!part && String(part).trim().length > 0)
		.join(', ');

const isBlank = (a: AddressData) =>
	!a.addressLine1.trim() && !a.city.trim() && !a.postalCode.trim();

interface AddressPickerProps {
	/**
	 * Whose address book to offer. Without one — or when the caller can't read it —
	 * the picker is just the custom form.
	 */
	organizationId?: string | null;
	value: AddressData;
	onChange: (address: AddressData) => void;
	errors?: AddressErrors;
	onCountryRulesChange?: (rules: { requireState: boolean; stateLabel: string }) => void;
	/** Gates the query while the containing dialog is closed. */
	skip?: boolean;
	/**
	 * Fill an empty value with the organization's primary address. Off for a
	 * surface where "no address" is a legitimate answer to leave alone.
	 */
	autoSelectPrimary?: boolean;
}

/**
 * Pick one of the organization's saved addresses, or write a different one.
 *
 * The picked address is the whole state — a saved row is selected by matching
 * the value against the address book, because what gets stored downstream is a
 * copy of the values either way, never a reference to the saved row.
 */
export function AddressPicker({
	organizationId,
	value,
	onChange,
	errors,
	onCountryRulesChange,
	skip,
	autoSelectPrimary = true,
}: AddressPickerProps) {
	const { data, error } = useGetOrganizationAddressesQuery({
		variables: { organizationId: organizationId ?? '' },
		skip: !!skip || !organizationId,
		fetchPolicy: 'no-cache',
	});

	const saved = useMemo(() => data?.getOrganizationAddresses ?? [], [data]);

	// Explicit intent to write a different address. Without it, clearing the form
	// back to a saved address's values would snap the selection back to that row.
	const [writingCustom, setWritingCustom] = useState(false);

	const matched = saved.find((a) => sameAddress(a, value)) ?? null;
	const mode: 'saved' | 'custom' = !writingCustom && matched ? 'saved' : 'custom';

	/**
	 * Seeded per organization, not once: on the create-quote form the client is
	 * chosen above this, and switching client has to bring their address book with
	 * it — leaving the previous client's address selected is how an order ships to
	 * the wrong company.
	 */
	const seededFor = useRef<string | null>(null);
	useEffect(() => {
		// Forgotten while the dialog is shut, so reopening seeds again — a picker
		// that survives the close would otherwise never fill the emptied form.
		if (skip) {
			seededFor.current = null;
			return;
		}
		if (!organizationId) return;
		// Wait for an answer. An error is one: the address book may not be
		// readable, and the custom form still has to work.
		if (!data && !error) return;
		if (seededFor.current === organizationId) return;

		const seeding = seededFor.current;
		seededFor.current = organizationId;

		// A value already filled in stays — except when it belonged to the client
		// that was selected a moment ago.
		const switchedClient = seeding !== null;
		if (!isBlank(value) && !switchedClient) return;

		const primary = autoSelectPrimary
			? (saved.find((a) => a.isPrimary) ?? saved[0])
			: undefined;
		if (primary) {
			setWritingCustom(false);
			onChange(toAddressData(primary));
		} else if (switchedClient && !isBlank(value)) {
			// The new client has nothing saved, so the old client's address is
			// left standing on screen. Clear it rather than let it be submitted.
			setWritingCustom(true);
			onChange(EMPTY_ADDRESS);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [organizationId, data, error, skip, saved]);

	return (
		<div className="flex flex-col gap-1.5 rounded-lg border border-line-1 p-1.5">
			{saved.map((a) => {
				const active = mode === 'saved' && matched?.id === a.id;
				return (
					<button
						key={a.id}
						type="button"
						onClick={() => {
							setWritingCustom(false);
							onChange(toAddressData(a));
						}}
						className={cn(
							'flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
							active ? 'bg-accent-soft' : 'hover:bg-surface-hover'
						)}
					>
						<Radio checked={active} className="mt-0.5" />
						<span className="min-w-0 flex-1">
							<span className="flex items-center gap-1.5">
								<span className="text-sm font-medium text-text-1">
									{a.name || a.addressLine1}
								</span>
								{a.isPrimary && (
									<span className="rounded-full bg-surface-fill px-1.5 py-0.5 text-2xs text-text-3">
										Primary
									</span>
								)}
							</span>
							<span className="mt-0.5 block truncate text-xs text-text-4">
								{addressSummary(a)}
							</span>
						</span>
					</button>
				);
			})}

			{/* Always offered, and the only option when nothing is saved. */}
			<button
				type="button"
				onClick={() => setWritingCustom(true)}
				className={cn(
					'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
					mode === 'custom' ? 'bg-accent-soft' : 'hover:bg-surface-hover'
				)}
			>
				<Radio checked={mode === 'custom'} />
				<span className="flex items-center gap-1.5 text-sm text-text-1">
					{saved.length > 0 ? (
						<>
							<Pencil size={11} className="text-text-4" />
							Ship somewhere else
						</>
					) : (
						<>
							<MapPin size={11} className="text-text-4" />
							Enter a delivery address
						</>
					)}
				</span>
			</button>

			{mode === 'custom' && (
				<div className="border-t border-line-1 px-2.5 pb-1.5 pt-3">
					<AddressForm
						value={value}
						onChange={onChange}
						errors={errors}
						onCountryRulesChange={onCountryRulesChange}
					/>
				</div>
			)}
		</div>
	);
}
